// Mutation queue for sync implementation
// Provides infrastructure for queuing, retrying, and managing offline mutations
// Hardened with idempotency, duplicate prevention, ordering guarantees, and crash safety

import { syncQueueRepository } from '../db/repositories.js';
import { offlineStateService } from '../services/offline-state.service.js';
import { OfflineConfig } from '../config/offline-config.js';
import { multiTabCoordinator } from '../services/multi-tab-coordination.service.js';

// Temporarily disable sync utilities to prevent circular dependency issues
// TODO: Re-enable after sync system is fully integrated
// import { 
//   MutationStatus, 
//   ErrorCategory,
//   DEFAULT_RETRY_CONFIG 
// } from '../sync/types/sync-types.js';
// import { 
//   generateMutationId, 
//   generateIdempotencyKey, 
//   sortMutations, 
//   groupMutationsByEntity,
//   canCoalesceMutations,
//   coalesceMutations,
//   validateMutation
// } from '../sync/utils/sync-utils.js';
// import { 
//   categorizeError, 
//   calculateRetryDelay, 
//   shouldRetry 
// } from '../sync/utils/retry-utils.js';

// Temporary fallback constants
const MutationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SYNCED: 'synced',
  FAILED: 'failed',
  CONFLICT: 'conflict',
  DEAD_LETTER: 'dead-letter',
};

const ErrorCategory = {
  TRANSIENT: 'transient',
  PERMANENT: 'permanent',
  VALIDATION: 'validation',
  AUTHORIZATION: 'authorization',
  CONFLICT: 'conflict',
};

const DEFAULT_RETRY_CONFIG = {
  maxRetries: 5,
  baseDelay: 2000,
  maxDelay: 30000,
  jitterFactor: 0.3,
};

// Temporary fallback functions
function generateMutationId() {
  return `mutation:${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function safeStringify(value) {
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      if (typeof val === 'bigint') return val.toString();
      if (val instanceof Blob || val instanceof File) return '[Binary]';
      return val;
    });
  } catch {
    return '[Unserializable]';
  }
}

/**
 * Simple string hash (DJB2) that produces a short, fixed-length hex string.
 * Used to hash the payload portion of idempotency keys so the key stays
 * small and ASCII-safe regardless of payload content (e.g. Persian text).
 */
function hashPayload(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return (hash >>> 0).toString(16);
}

function generateIdempotencyKey(operation, entityType, entityId, payload) {
  const payloadStr = safeStringify(payload);
  const payloadHash = hashPayload(payloadStr);
  return `${operation}:${entityType}:${entityId}:${payloadHash}`;
}

function sortMutations(mutations) {
  return mutations.sort((a, b) => {
    // Primary sort: explicit sequence number (assigned at queue time)
    const seqA = a.sequenceNumber ?? a.createdAt ?? 0;
    const seqB = b.sequenceNumber ?? b.createdAt ?? 0;
    if (seqA !== seqB) return seqA - seqB;
    // Fallback: createdAt timestamp
    const timestampA = a.createdAt || 0;
    const timestampB = b.createdAt || 0;
    return timestampA - timestampB;
  });
}

function groupMutationsByEntity(mutations) {
  const groups = new Map();
  
  for (const mutation of mutations) {
    const key = `${mutation.entityType}:${mutation.entityId}`;
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    
    groups.get(key).push(mutation);
  }
  
  return groups;
}

function canCoalesceMutations(mutation1, mutation2) {
  if (mutation1.entityType !== mutation2.entityType ||
      mutation1.entityId !== mutation2.entityId) {
    return false;
  }
  
  if (mutation1.operationType !== 'update' || 
      mutation2.operationType !== 'update') {
    return false;
  }
  
  const timeDiff = Math.abs(mutation1.createdAt - mutation2.createdAt);
  if (timeDiff > 5000) {
    return false;
  }
  
  return true;
}

function coalesceMutations(mutations) {
  if (mutations.length < 2) {
    return mutations;
  }
  
  const coalesced = [];
  const sorted = sortMutations([...mutations]);
  
  let current = sorted[0];
  
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    
    if (canCoalesceMutations(current, next)) {
      current = {
        ...current,
        payload: { ...current.payload, ...next.payload },
        createdAt: next.createdAt,
        idempotencyKey: generateIdempotencyKey(
          current.operationType,
          current.entityType,
          current.entityId,
          { ...current.payload, ...next.payload }
        ),
      };
    } else {
      coalesced.push(current);
      current = next;
    }
  }
  
  coalesced.push(current);
  return coalesced;
}

function validateMutation(mutation) {
  const required = ['mutationId', 'entityType', 'entityId', 'operationType', 'payload', 'idempotencyKey'];
  const missing = required.filter(field => !mutation[field]);
  
  if (missing.length > 0) {
    return { valid: false, missing };
  }
  
  const validOperations = ['create', 'update', 'delete'];
  if (!validOperations.includes(mutation.operationType)) {
    return { valid: false, reason: 'invalid_operation' };
  }
  
  return { valid: true };
}

function categorizeError(error) {
  if (!error) return ErrorCategory.TRANSIENT;

  if (error.response?.status) {
    const status = error.response.status;
    
    if ([408, 429, 500, 502, 503, 504].includes(status)) {
      return ErrorCategory.TRANSIENT;
    }
    
    if ([400, 401, 403, 404, 422].includes(status)) {
      if (status === 401 || status === 403) {
        return ErrorCategory.AUTHORIZATION;
      }
      if (status === 400 || status === 422) {
        return ErrorCategory.VALIDATION;
      }
      return ErrorCategory.PERMANENT;
    }
    
    if ([409].includes(status)) {
      return ErrorCategory.CONFLICT;
    }
  }

  if (error.message?.includes('network') || 
      error.message?.includes('timeout') ||
      error.message?.includes('fetch')) {
    return ErrorCategory.TRANSIENT;
  }

  return ErrorCategory.TRANSIENT;
}

function calculateRetryDelay(retryCount, config = DEFAULT_RETRY_CONFIG) {
  const { baseDelay, maxDelay, jitterFactor } = config;
  
  const exponentialDelay = baseDelay * Math.pow(2, retryCount);
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  const jitter = 1 + (Math.random() * 2 - 1) * jitterFactor;
  
  return Math.floor(cappedDelay * jitter);
}

function shouldRetry(error, retryCount, config = DEFAULT_RETRY_CONFIG) {
  const category = categorizeError(error);
  
  if (category === ErrorCategory.PERMANENT) {
    return false;
  }
  
  if (category === ErrorCategory.VALIDATION) {
    return false;
  }
  
  if (category === ErrorCategory.AUTHORIZATION) {
    return false;
  }
  
  if (category === ErrorCategory.CONFLICT) {
    return false;
  }
  
  if (retryCount >= config.maxRetries) {
    return false;
  }
  
  return true;
}

class MutationQueue {
  constructor() {
    this.processing = false;
    this.maxRetries = OfflineConfig.queue.maxRetryCount;
    this.retryDelay = OfflineConfig.queue.retryDelayBase;
    this.processedIds = new Set(); // Track recently processed for deduplication
    this.idempotencyKeys = new Map(); // Track idempotency keys
    this.entityMutationOrder = new Map(); // Track mutation order per entity
    this.subscribers = new Set();
    this._keysLoaded = false;
  }

  /**
   * Warm the in-memory idempotency key cache from IndexedDB so
   * duplicate detection works across page reloads without blocking enqueue.
   * Called lazily before the first duplicate check — fire-and-forget with a
   * 3-second timeout so slow IndexedDB never blocks the critical path.
   */
  async _ensureKeysLoaded() {
    if (this._keysLoaded) return;
    this._keysLoaded = true;
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 3000)
      );
      const allKeys = await Promise.race([syncQueueRepository.getKeys(), timeout]);
      const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));
      const entries = await Promise.all(
        mutationKeys.map(key =>
          Promise.race([
            syncQueueRepository.getById(key),
            new Promise(resolve => setTimeout(() => resolve(null), 2000)),
          ]).catch(() => null)
        )
      );
      for (const entry of entries) {
        if (entry?.data?.idempotencyKey) {
          this.idempotencyKeys.set(entry.data.idempotencyKey, entry.data.createdAt || Date.now());
        }
      }
    } catch (err) {
      console.warn('[MutationQueue] Failed to preload idempotency keys (non-blocking):', err);
    }
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /** Do not await: UI subscribers must never block enqueue / markAsSynced. */
  scheduleNotifySubscribers() {
    void Promise.resolve()
      .then(() => this.notifySubscribers())
      .catch((err) => console.error('[MutationQueue] notifySubscribers:', err));
  }

  async notifySubscribers() {
    for (const callback of this.subscribers) {
      try {
        await callback();
      } catch (error) {
        console.error('[MutationQueue] Subscriber callback failed:', error);
      }
    }
  }

  /**
   * Check if mutation is duplicate — uses in-memory cache only.
   * Full IndexedDB scan was removed because it blocks every enqueue with
   * O(n) reads and caused the mutation to hang indefinitely under load.
   * Server-side idempotency (IdempotencyKey model) is the source of truth
   * for dedup across sessions. The lazy preload (_ensureKeysLoaded) is
   * fire-and-forget so it never blocks the critical path.
   */
  async isDuplicate(mutation) {
    const idempotencyKey = generateIdempotencyKey(
      mutation.operationType || mutation.operation,
      mutation.entityType,
      mutation.entityId,
      mutation.payload
    );
    // Kick off lazy IndexedDB preload but never block on it
    this._ensureKeysLoaded().catch(() => {});
    return this.idempotencyKeys.has(idempotencyKey);
  }

  /**
   * Add mutation to queue with full metadata
   */
  async enqueue(mutation) {
    // Normalize mutation structure
    const normalizedMutation = {
      mutationId: mutation.mutationId || generateMutationId(),
      entityType: mutation.entityType,
      entityId: mutation.entityId,
      operationType: mutation.operationType || mutation.operation,
      payload: mutation.payload,
      idempotencyKey: generateIdempotencyKey(
        mutation.operationType || mutation.operation,
        mutation.entityType,
        mutation.entityId,
        mutation.payload
      ),
      createdAt: mutation.createdAt || Date.now(),
      sequenceNumber: 0, // placeholder; real value assigned below after persistence
      retryCount: 0,
      lastAttemptAt: null,
      status: MutationStatus.PENDING,
      dependencies: mutation.dependencies || [],
      metadata: mutation.metadata || {},
      url: mutation.url,
      method: mutation.method,
      headers: mutation.headers,
    };

    // Validate mutation
    const validation = validateMutation(normalizedMutation);
    if (!validation.valid) {
      console.error('[MutationQueue] Invalid mutation:', validation);
      throw new Error(`Invalid mutation: ${validation.missing?.join(', ') || validation.reason}`);
    }

    // Check for duplicates
    if (await this.isDuplicate(normalizedMutation)) {
      console.log('[MutationQueue] Duplicate mutation detected, skipping');
      return null;
    }

    // Check for coalescing with recent mutations
    const coalesced = await this.tryCoalesce(normalizedMutation);
    if (coalesced) {
      console.log('[MutationQueue] Coalesced with existing mutation');
      return coalesced;
    }

    // ── Dependency validation & cycle detection ─────────────────────────
    // Verify each dependency exists and is a pending CREATE.  If a dependency
    // has already been removed from the queue (synced), treat it as satisfied.
    // Detect direct cycles: if another pending mutation already lists this
    // mutation's ID as a dependency, log a warning and remove the cycle.
    if (normalizedMutation.dependencies?.length > 0) {
      const valid = [];
      for (const depId of normalizedMutation.dependencies) {
        const depEntry = await syncQueueRepository.getById(depId);
        if (!depEntry) {
          // Dependency already removed (synced) — treat as satisfied, skip.
          continue;
        }
        if (depEntry.data?.status === MutationStatus.PENDING ||
            depEntry.data?.status === MutationStatus.PROCESSING) {
          // Check indirect cycle: does the dependency also depend on us?
          // Since our mutationId was just generated, no existing mutation can
          // reference it directly, but check the full chain for safety.
          const depDeps = depEntry.data?.dependencies || [];
          if (depDeps.includes(normalizedMutation.mutationId)) {
            console.warn(
              `[MutationQueue] Cycle detected: ${depId} already depends on ${normalizedMutation.mutationId}, removing dependency`
            );
            continue;
          }
          valid.push(depId);
        } else {
          // Dependency exists but is in terminal state (failed / conflict / dead-letter)
          console.warn(`[MutationQueue] Dependency ${depId} is in terminal state ${depEntry.data?.status}, keeping dependency — parent will need manual resolution`);
          valid.push(depId);
        }
      }
      normalizedMutation.dependencies = valid;
    }

    // Assign a global sequence number BEFORE persistence so the sort is deterministic.
    // The sequence counter is stored in IndexedDB and survives page reloads, ensuring
    // mutations are always processed in the exact order they were queued.
    const sequenceNumber = await syncQueueRepository.getNextSequence();
    normalizedMutation.sequenceNumber = sequenceNumber;

    // Add to queue — mutation is persisted with its sequence number already set
    const result = await syncQueueRepository.enqueue(normalizedMutation);
    
    // Track locally
    this.idempotencyKeys.set(normalizedMutation.idempotencyKey, Date.now());
    
    // Track entity mutation order
    const entityKey = `${normalizedMutation.entityType}:${normalizedMutation.entityId}`;
    if (!this.entityMutationOrder.has(entityKey)) {
      this.entityMutationOrder.set(entityKey, []);
    }
    this.entityMutationOrder.get(entityKey).push(normalizedMutation.mutationId);
    
    // Cleanup old idempotency keys (keep for 1 hour)
    this.cleanupIdempotencyKeys(3600000);

    console.log('[MutationQueue] Enqueued mutation:', normalizedMutation.operationType, normalizedMutation.entityType);
    offlineStateService.incrementPendingOperations();
    
    // Notify other tabs and subscribers
    multiTabCoordinator.notifyMutationQueued(normalizedMutation.mutationId);
    this.scheduleNotifySubscribers();
    
    return result;
  }

  /**
   * Try to coalesce with existing pending mutations
   */
  async tryCoalesce(newMutation) {
    const pending = await this.getPending();
    
    for (const existing of pending) {
      if (canCoalesceMutations(existing, newMutation)) {
        // Coalesce by updating the existing mutation
        const coalescedPayload = { ...existing.payload, ...newMutation.payload };
        // Merge dependency arrays (deduplicated) so the coalesced mutation
        // cannot outrun any parent CREATE still pending in the queue.
        const mergedDeps = [
          ...new Set([
            ...(existing.dependencies || []),
            ...(newMutation.dependencies || []),
          ]),
        ];
        const coalescedMutation = {
          ...existing,
          payload: coalescedPayload,
          dependencies: mergedDeps,
          idempotencyKey: generateIdempotencyKey(
            existing.operationType,
            existing.entityType,
            existing.entityId,
            coalescedPayload
          ),
          createdAt: newMutation.createdAt,
        };
        
        await syncQueueRepository.update(existing.mutationId, coalescedMutation);
        
        // Update local tracking
        this.idempotencyKeys.set(coalescedMutation.idempotencyKey, Date.now());
        
        return coalescedMutation;
      }
    }
    
    return null;
  }

  /**
   * Cleanup old idempotency keys
   */
  cleanupIdempotencyKeys(maxAge) {
    const now = Date.now();
    for (const [key, timestamp] of this.idempotencyKeys.entries()) {
      if (now - timestamp > maxAge) {
        this.idempotencyKeys.delete(key);
      }
    }
  }

  /**
   * Get all pending mutations with proper ordering.
   * Includes a 4-second overall guard so slow IndexedDB never freezes the UI.
   */
  async getPending() {
    try {
      const withTimeout = (promise, ms = 4000) =>
        Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms)),
        ]);

      const allKeys = await withTimeout(syncQueueRepository.getKeys());
      const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));
      
      const mutations = await Promise.all(
        mutationKeys.map(async (key) => {
          try {
            const entry = await withTimeout(syncQueueRepository.getById(key), 2000);
            return entry?.data;
          } catch {
            return null;
          }
        })
      );

      const pending = mutations.filter(m => m?.status === MutationStatus.PENDING);
      return sortMutations(pending);
    } catch (err) {
      console.error('[MutationQueue] getPending failed:', err);
      return [];
    }
  }

  /**
   * Get mutations for a specific entity in order
   */
  async getEntityMutations(entityType, entityId) {
    const pending = await this.getPending();
    return pending.filter(m => m.entityType === entityType && m.entityId === entityId);
  }

  /**
   * Process pending mutations with ordering and crash safety
   * This method is now a placeholder - actual processing is done by SyncEngine
   */
  async processPending() {
    if (this.processing) {
      console.log('[MutationQueue] Already processing');
      return;
    }

    if (!offlineStateService.isCurrentlyOnline()) {
      console.log('[MutationQueue] Offline, skipping processing');
      return;
    }

    this.processing = true;
    console.log('[MutationQueue] Processing pending mutations');

    try {
      const pending = await this.getPending();
      
      // Group by entity to ensure ordering per entity
      const grouped = groupMutationsByEntity(pending);
      
      let processedCount = 0;
      
      for (const [entityKey, mutations] of grouped.entries()) {
        // Process mutations for this entity in order
        for (const mutation of mutations) {
          try {
            // Skip if already processed recently
            if (this.processedIds.has(mutation.mutationId)) {
              continue;
            }

            // Check dependencies
            if (mutation.dependencies && mutation.dependencies.length > 0) {
              const dependenciesMet = await this.checkDependencies(mutation.dependencies);
              if (!dependenciesMet) {
                console.log('[MutationQueue] Dependencies not met for:', mutation.mutationId);
                continue;
              }
            }

            // Placeholder for actual sync logic
            // This will be replaced by SyncEngine.processMutation
            console.log('[MutationQueue] Would sync mutation:', mutation.mutationId);
            
            // Mark as synced (placeholder)
            await this.markAsSynced(mutation.mutationId);
            
            // Track as processed
            this.processedIds.add(mutation.mutationId);
            offlineStateService.decrementPendingOperations();
            processedCount++;
          } catch (error) {
            console.error('[MutationQueue] Mutation failed:', mutation.mutationId, error);
            
            await this.handleMutationFailure(mutation, error);
          }
        }
      }

      // Notify other tabs
      if (processedCount > 0) {
        multiTabCoordinator.notifyQueueProcessed(processedCount);
      }
    } finally {
      this.processing = false;
    }
  }

  /**
   * Check if mutation dependencies are met
   */
  async checkDependencies(dependencies) {
    for (const depId of dependencies) {
      const entry = await syncQueueRepository.getById(depId);
      if (entry?.data?.status !== MutationStatus.SYNCED) {
        return false;
      }
    }
    return true;
  }

  /**
   * Update mutation status (delegates to persistent queue store)
   */
  async updateStatus(mutationId, status, retryCount) {
    await syncQueueRepository.updateStatus(mutationId, status, retryCount);
    this.scheduleNotifySubscribers();
  }

  /**
   * Mark mutation as synced
   */
  async markAsSynced(mutationId) {
    await syncQueueRepository.updateStatus(mutationId, MutationStatus.SYNCED, 0);
    await syncQueueRepository.remove(mutationId);
    
    offlineStateService.decrementPendingOperations();

    // Remove from entity order tracking
    for (const [entityKey, mutationIds] of this.entityMutationOrder.entries()) {
      const index = mutationIds.indexOf(mutationId);
      if (index > -1) {
        mutationIds.splice(index, 1);
        if (mutationIds.length === 0) {
          this.entityMutationOrder.delete(entityKey);
        }
      }
    }

    this.scheduleNotifySubscribers();
  }

  /**
   * Handle mutation failure with proper categorization
   */
  async handleMutationFailure(mutation, error) {
    const category = categorizeError(error);
    const retryCount = (mutation.retryCount || 0) + 1;
    
    if (category === ErrorCategory.CONFLICT) {
      await syncQueueRepository.updateStatus(mutation.mutationId, MutationStatus.CONFLICT, retryCount);
    } else if (!shouldRetry(error, retryCount, DEFAULT_RETRY_CONFIG)) {
      await syncQueueRepository.updateStatus(mutation.mutationId, MutationStatus.FAILED, retryCount);
      await this.moveToDeadLetter(mutation);
    } else {
      await syncQueueRepository.updateStatus(mutation.mutationId, MutationStatus.PROCESSING, retryCount);
      
      // Update last attempt time and calculate next retry
      const updatedMutation = {
        ...mutation,
        retryCount,
        lastAttemptAt: Date.now(),
      };
      await syncQueueRepository.update(mutation.mutationId, updatedMutation);
    }
  }

  /**
   * Move failed mutation to dead letter
   */
  async moveToDeadLetter(mutation) {
    console.log('[MutationQueue] Moving to dead letter:', mutation.mutationId);
    await syncQueueRepository.updateStatus(mutation.mutationId, MutationStatus.DEAD_LETTER, mutation.retryCount || 0);
  }

  /**
   * Retry failed mutations — gives each mutation up to SESSION_MAX retries per sync session.
   *
   * - Within one sync session: retries up to SESSION_MAX (3) times, then leaves as FAILED.
   * - When user triggers sync again (syncNow / page reload): sessionRetryCount resets to 0,
   *   giving the mutation another SESSION_MAX retries.
   * - If total retries across all sessions exceeds TOTAL_MAX_RETRIES (9), moves to DEAD_LETTER.
   *   Any dependent mutations are also cascaded to DEAD_LETTER.
   */
  async retryFailed() {
    const SESSION_MAX = 3;
    const TOTAL_MAX_RETRIES = 9;

    const allKeys = await syncQueueRepository.getKeys();
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

    const retried = [];
    const deadLettered = [];
    for (const key of mutationKeys) {
      try {
        const entry = await syncQueueRepository.getById(key);
        if (!entry?.data) continue;

        const status = entry.data.status;
        if (status !== MutationStatus.FAILED) continue;

        const sessionRetryCount = entry.data.sessionRetryCount || 0;
        const retryCount = entry.data.retryCount || 0;

        // Total lifetime retries exceeded — move to dead letter permanently
        if (retryCount >= TOTAL_MAX_RETRIES) {
          console.log('[MutationQueue] Moving to dead letter (total retries exceeded):', key);
          await syncQueueRepository.updateStatus(key, MutationStatus.DEAD_LETTER, retryCount);
          deadLettered.push(entry);
          // Cascade: any mutation that depends on this one cannot succeed either
          await this.cascadeDeadLetter(key);
          continue;
        }

        // Session retries exceeded — leave as FAILED until next session
        if (sessionRetryCount >= SESSION_MAX) {
          continue;
        }

        // Reset to pending for another try in this session
        await syncQueueRepository.updateStatus(key, MutationStatus.PENDING, retryCount);
        retried.push(entry);
      } catch {
        // skip individual read/write errors
      }
    }

    if (deadLettered.length > 0) {
      console.log('[MutationQueue] Moved', deadLettered.length, 'mutation(s) to dead letter (permanent failures)');
    }
    if (retried.length > 0) {
      console.log('[MutationQueue] Retried', retried.length, 'failed mutation(s)');
    }
    this.scheduleNotifySubscribers();
    return retried.length;
  }

  /**
   * Cascade DEAD_LETTER status to all mutations that depend on the given parent.
   * If a parent mutation can never succeed (DEAD_LETTER), any child that lists it
   * as a dependency is also permanently blocked and moved to DEAD_LETTER.
   */
  async cascadeDeadLetter(parentMutationId) {
    try {
      const allKeys = await syncQueueRepository.getKeys();
      const mutationKeys = allKeys.filter(k => k.startsWith('mutation:'));
      let cascaded = 0;

      for (const key of mutationKeys) {
        try {
          const entry = await syncQueueRepository.getById(key);
          if (!entry?.data) continue;
          if (entry.data.status === MutationStatus.DEAD_LETTER ||
              entry.data.status === MutationStatus.SYNCED) continue;

          const deps = entry.data.dependencies || [];
          if (deps.includes(parentMutationId)) {
            await syncQueueRepository.updateStatus(key, MutationStatus.DEAD_LETTER, entry.data.retryCount || 0);
            cascaded++;
            console.log(`[MutationQueue] Cascaded DEAD_LETTER to dependent mutation ${key}`);
            // Recursively cascade to any mutations that depend on this child
            await this.cascadeDeadLetter(key);
          }
        } catch {
          // skip individual read/write errors
        }
      }

      if (cascaded > 0) {
        console.log(`[MutationQueue] Cascaded DEAD_LETTER to ${cascaded} dependent mutation(s) of ${parentMutationId}`);
      }
    } catch (err) {
      console.error('[MutationQueue] cascadeDeadLetter failed:', err);
    }
  }

  /**
   * Reset sessionRetryCount to 0 for all FAILED mutations so they get another
   * SESSION_MAX (3) retries in the next sync session.
   * Called when the user explicitly triggers "Sync Now" or on app init.
   */
  async resetSessionRetryCounts() {
    const allKeys = await syncQueueRepository.getKeys();
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));
    let reset = 0;

    for (const key of mutationKeys) {
      try {
        const entry = await syncQueueRepository.getById(key);
        if (!entry?.data) continue;
        const status = entry.data.status;
        if (status !== MutationStatus.FAILED && status !== MutationStatus.PENDING) continue;

        const currentSession = entry.data.sessionRetryCount || 0;
        if (currentSession > 0) {
          await syncQueueRepository.update(key, { sessionRetryCount: 0 });
          reset++;
        }
      } catch {
        // skip
      }
    }

    if (reset > 0) {
      console.log('[MutationQueue] Reset sessionRetryCount for', reset, 'mutation(s)');
    }
    return reset;
  }

  /**
   * Clear all mutations
   */
  async clearAll() {
    const count = await syncQueueRepository.clearPending();
    this.processedIds.clear();
    this.idempotencyKeys.clear();
    offlineStateService.decrementPendingOperations();
    console.log('[MutationQueue] Cleared all mutations');
    this.scheduleNotifySubscribers();
    return count;
  }

  /**
   * Get queue statistics — wrapped in a 5-second guard so a stalled
   * IndexedDB never blocks the settings page or sync-status polling.
   */
  async getStats() {
    try {
      const pending = await this.getPending();
      const allKeys = await syncQueueRepository.getKeys();
      const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

      const safeRead = async (key) => {
        try {
          const entry = await Promise.race([
            syncQueueRepository.getById(key),
            new Promise(resolve => setTimeout(() => resolve(null), 2000)),
          ]);
          return entry?.data;
        } catch {
          return null;
        }
      };

      const allMutations = await Promise.all(mutationKeys.map(safeRead));

      return {
        total: allMutations.length,
        pending: pending.length,
        processing: allMutations.filter(m => m?.status === MutationStatus.PROCESSING).length,
        synced: allMutations.filter(m => m?.status === MutationStatus.SYNCED).length,
        failed: allMutations.filter(m => m?.status === MutationStatus.FAILED).length,
        conflict: allMutations.filter(m => m?.status === MutationStatus.CONFLICT).length,
        deadLetter: allMutations.filter(m => m?.status === MutationStatus.DEAD_LETTER).length,
      };
    } catch (err) {
      console.error('[MutationQueue] getStats failed:', err);
      return { total: 0, pending: 0, processing: 0, synced: 0, failed: 0, conflict: 0, deadLetter: 0 };
    }
  }

  /**
   * Get all mutations with full details, sorted by createdAt ascending.
   */
  async getAllMutations() {
    try {
      const allKeys = await syncQueueRepository.getKeys();
      const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));

      const safeRead = async (key) => {
        try {
          const entry = await Promise.race([
            syncQueueRepository.getById(key),
            new Promise(resolve => setTimeout(() => resolve(null), 2000)),
          ]);
          return entry?.data;
        } catch {
          return null;
        }
      };

      const mutations = await Promise.all(mutationKeys.map(safeRead));
      return sortMutations(mutations.filter(Boolean));
    } catch (err) {
      console.error('[MutationQueue] getAllMutations failed:', err);
      return [];
    }
  }

  /**
   * Validate queue integrity
   */
  async validateQueue() {
    const allKeys = await syncQueueRepository.getKeys();
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));
    
    const issues = [];
    
    for (const key of mutationKeys) {
      const entry = await syncQueueRepository.getById(key);
      
      // Check for missing required fields
      if (!entry?.data?.mutationId) {
        issues.push({ key, issue: 'missing_mutationId' });
      }
      if (!entry?.data?.operationType) {
        issues.push({ key, issue: 'missing_operationType' });
      }
      if (!entry?.data?.entityType) {
        issues.push({ key, issue: 'missing_entityType' });
      }
      if (!entry?.data?.entityId) {
        issues.push({ key, issue: 'missing_entityId' });
      }
      if (!entry?.data?.idempotencyKey) {
        issues.push({ key, issue: 'missing_idempotencyKey' });
      }
      
      // Check for stale entries
      const age = Date.now() - (entry?.data?.createdAt || 0);
      if (age > OfflineConfig.queue.deadLetterMaxAge) {
        issues.push({ key, issue: 'stale', age });
      }
    }

    return issues;
  }

  /**
   * Start automatic processing when online
   */
  startAutoProcessing() {
    const unsubscribe = offlineStateService.subscribe((state) => {
      if (state.isOnline && state.pendingOperations > 0) {
        // Delay slightly to ensure connection is stable
        setTimeout(() => this.processPending(), 1000);
      }
    });

    // Also listen for queue processed from other tabs
    multiTabCoordinator.subscribe('QUEUE_PROCESSED', (data) => {
      console.log('[MutationQueue] Queue processed in another tab:', data.count);
      // Refresh local state
      offlineStateService.decrementPendingOperations();
    });

    return unsubscribe;
  }
}

// Export singleton instance
export const mutationQueue = new MutationQueue();
